import { AuroraCachedApi } from './aurora-cache';
import { SubscriptionService } from './subscription-service';
import type { Station } from './aurora-api';
import { t } from '../translations';
import { LocalStorageService } from './local-storage';

export interface UserLocation {
  latitude: number;
  longitude: number;
}

export interface ClosestStation {
  station: Station;
  distance: number; // in kilometers
}

export interface StationSelection {
  mode: 'automatic' | 'manual';
  stationCodes: string[];
}

export class LocationService {
  public static readonly MAX_NOTIFICATION_DISTANCE = 500; // km

  private static instance: LocationService;

  private constructor() {}

  public static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  /**
   * Calculate distance between two points using Haversine formula
   */
  static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Find the closest station to the user's location
   */
  static async findClosestStation(userLocation: UserLocation): Promise<ClosestStation | null> {
    try {
      const stations = await AuroraCachedApi.getStations();

      if (stations.length === 0) {
        console.warn('No stations available for distance calculation');
        return null;
      }

      let closestStation: Station | null = null;
      let minDistance = Infinity;

      for (const station of stations) {
        const distance = this.calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          station.coordinates.lat,
          station.coordinates.long
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestStation = station;
        }
      }

      if (!closestStation) {
        return null;
      }

      return {
        station: closestStation,
        distance: Math.round(minDistance * 10) / 10 // Round to 1 decimal place
      };
    } catch (error) {
      console.error('Error finding closest station:', error);
      return null;
    }
  }

  /**
   * Main method to process user location and handle station tracking
   * Call this when location is granted and user location is fetched
   */
  async processUserLocation(userLocation: UserLocation): Promise<void> {

    const stationSelection = LocalStorageService.getStationSelection();
    if (stationSelection.mode !== 'automatic') {
      console.debug('📍 Station selection is manual - skipping automatic location processing');
      return;
    }

    const lastLocationUpdate = LocalStorageService.getLastLocationUpdate();
    console.debug('📍 Last location update:', lastLocationUpdate);
    if (lastLocationUpdate !== null && lastLocationUpdate.getTime() + 60 * 1000 >= Date.now()) {
      console.debug('📍 Last location update was less than 1 minute ago - skipping processing')
      return;
    }
    LocalStorageService.saveLastLocationUpdate(new Date());

    try {
      console.debug(`📍 Processing user location: ${userLocation.latitude}, ${userLocation.longitude}`);
      const subscriptionService = SubscriptionService.getInstance();

      // Get previously saved closest station
      const previousClosestStation = LocalStorageService.getClosestStationData();

      // Find closest station
      const closestStation = await LocationService.findClosestStation(userLocation);

      if (!closestStation) {
        console.warn('Could not find closest station');
        return;
      }

      // Check if station has changed
      const stationChanged = !previousClosestStation ||
        previousClosestStation.station.code !== closestStation.station.code;

      const hasSubscribedStation = LocalStorageService.getStations().length > 0;;
      console.debug(`Has subscribed station: ${hasSubscribedStation}`);

      // if closest station hasn't changed there's no need to update
      // unless subscribed station is not set, then we need to check if the station is within range
      if (!stationChanged && hasSubscribedStation) {
        console.debug('📍 Closest station and range status unchanged - no action needed');
        return;
      }

      // Save to local storage
      LocalStorageService.saveClosestStationData(closestStation);
      console.log(`📍 New closest station: ${closestStation.station.name} (${closestStation.distance} km)`);

      if (LocationService.isWithinNotificationRange(closestStation.distance)) {
        console.log(`📍 Station changed from ${previousClosestStation?.station.name || 'none'} to ${closestStation.station.name}`);
        await subscriptionService.updateStationList([closestStation.station.code]);
      } else {
        console.log(`📍 Closest station ${closestStation.station.name} is out of notification range (${closestStation.distance} km)`);
        await subscriptionService.updateStationList([]);
      }
    } catch (error) {
      console.error('Error processing user location:', error);
    }
  }

  /**
   * Get distance in a human-readable format
   */
  static formatDistance(distance: number): string {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    } else if (distance < 100) {
      return `${Math.round(distance * 10) / 10}km`;
    } else {
      return `${Math.round(distance)}km`;
    }
  }

  /**
   * Check if a station is within notification range
   */
  static isWithinNotificationRange(distance: number): boolean {
    return distance <= this.MAX_NOTIFICATION_DISTANCE;
  }

}